import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart3, TrendingUp, Eye, Users, Calendar, Mail, Settings } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalytics() {
  const { isAdmin } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [financialStats, setFinancialStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gaId, setGaId] = useState('');
  const [savingGa, setSavingGa] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/stats`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setGaId(settingsRes.data?.ga_tracking_id || '');

      // Fetch financial stats if admin
      if (isAdmin) {
        try {
          const financialRes = await axios.get(`${API}/admin/analytics/financial`, { withCredentials: true });
          setFinancialStats(financialRes.data);
        } catch (e) { /* Not admin */ }
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveGaSettings = async () => {
    setSavingGa(true);
    try {
      await axios.put(`${API}/settings`, {
        ...settings,
        ga_tracking_id: gaId,
        use_google_analytics: !!gaId
      }, { withCredentials: true });
      toast.success('Analytics settings saved');
      setSettings({ ...settings, ga_tracking_id: gaId });
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingGa(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasGA = settings?.ga_tracking_id;

  return (
    <div data-testid="admin-analytics">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-charcoal">Analytics</h1>
        <p className="text-charcoal/60 mt-1">
          {hasGA ? 'Google Analytics connected' : 'Built-in analytics tracking'}
        </p>
      </div>

      {/* Google Analytics Settings - Admin Only */}
      {isAdmin && (
        <div className="bg-white rounded-2xl p-6 border border-gold/10 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings size={20} className="text-burgundy" />
            <h2 className="font-serif text-lg text-charcoal">Analytics Configuration</h2>
          </div>
          <p className="text-sm text-charcoal/60 mb-4">
            Enter your Google Analytics 4 tracking ID to enable advanced analytics. 
            Leave empty to use basic built-in tracking.
          </p>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Google Analytics Tracking ID</Label>
              <Input
                value={gaId}
                onChange={(e) => setGaId(e.target.value)}
                placeholder="G-XXXXXXXXXX"
                className="bg-cream"
                data-testid="ga-tracking-id"
              />
            </div>
            <Button
              onClick={saveGaSettings}
              disabled={savingGa}
              className="bg-burgundy hover:bg-purple"
              data-testid="save-ga-btn"
            >
              {savingGa ? 'Saving...' : 'Save'}
            </Button>
          </div>
          {hasGA && (
            <p className="text-xs text-olive mt-2">✓ Google Analytics is active</p>
          )}
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Eye}
          label="Today's Views"
          value={stats?.page_views?.today || 0}
          color="bg-blue-500"
        />
        <StatCard
          icon={TrendingUp}
          label="This Week"
          value={stats?.page_views?.this_week || 0}
          color="bg-green-500"
        />
        <StatCard
          icon={Calendar}
          label="This Month"
          value={stats?.page_views?.this_month || 0}
          color="bg-purple"
        />
        <StatCard
          icon={Mail}
          label="Newsletter Subs"
          value={stats?.submissions?.newsletter || 0}
          color="bg-gold"
        />
      </div>

      {/* Financial Stats - Admin Only */}
      {isAdmin && financialStats && (
        <div className="bg-gradient-to-r from-burgundy to-purple rounded-2xl p-6 text-white mb-8">
          <h2 className="font-serif text-lg mb-4">Financial Overview (Admin Only)</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-white/70 text-sm">Total Donations</p>
              <p className="text-2xl font-bold">{financialStats.total_donations}</p>
            </div>
            <div>
              <p className="text-white/70 text-sm">Total Amount</p>
              <p className="text-2xl font-bold">${financialStats.total_amount?.toLocaleString() || 0}</p>
            </div>
            <div className="col-span-2">
              <p className="text-white/70 text-sm mb-2">Monthly Trend</p>
              <div className="flex gap-2">
                {financialStats.monthly_trend?.map((m, i) => (
                  <div key={i} className="flex-1 text-center">
                    <div 
                      className="bg-white/20 rounded-t"
                      style={{ height: `${Math.max(20, (m.amount / Math.max(...financialStats.monthly_trend.map(x => x.amount || 1))) * 60)}px` }}
                    />
                    <p className="text-xs mt-1">{m.month?.split(' ')[0]}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-olive" />
            Daily Views (Last 7 Days)
          </h2>
          {stats?.daily_views?.length > 0 ? (
            <div className="flex items-end gap-2 h-32">
              {stats.daily_views.map((day, i) => {
                const maxViews = Math.max(...stats.daily_views.map(d => d.views || 1));
                const height = Math.max(10, (day.views / maxViews) * 100);
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-olive/80 rounded-t transition-all duration-300 hover:bg-olive"
                      style={{ height: `${height}%` }}
                      title={`${day.views} views`}
                    />
                    <p className="text-xs text-charcoal/50 mt-2">{new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}</p>
                    <p className="text-xs font-medium text-charcoal">{day.views}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-charcoal/50 text-sm">No view data yet</p>
          )}
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-gold" />
            Top Pages
          </h2>
          {stats?.top_pages?.length > 0 ? (
            <div className="space-y-3">
              {stats.top_pages.map((page, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 bg-cream rounded-full flex items-center justify-center text-xs font-medium text-charcoal/60">
                      {i + 1}
                    </span>
                    <span className="text-sm text-charcoal">{page._id || '/'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-charcoal">{page.count}</span>
                    <span className="text-xs text-charcoal/40">views</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-charcoal/50 text-sm">No page view data yet</p>
          )}
        </div>

        {/* Submission Breakdown */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4 flex items-center gap-2">
            <Users size={20} className="text-burgundy" />
            Form Submissions
          </h2>
          <div className="space-y-3">
            <SubmissionRow label="Contact Messages" value={stats?.submissions?.contacts || 0} />
            <SubmissionRow label="Volunteer Applications" value={stats?.submissions?.volunteers || 0} />
            <SubmissionRow label="Prayer Requests" value={stats?.submissions?.prayers || 0} />
            <SubmissionRow label="Newsletter Subscribers" value={stats?.submissions?.newsletter || 0} />
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl p-6 border border-gold/10">
          <h2 className="font-serif text-lg text-charcoal mb-4">Recent Activity</h2>
          {stats?.recent_activity?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {stats.recent_activity.slice(0, 15).map((event, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-cream/50 rounded-lg">
                  <div className={`w-2 h-2 rounded-full ${
                    event.event_type === 'donation' ? 'bg-green-500' :
                    event.event_type === 'form_submit' ? 'bg-blue-500' :
                    'bg-charcoal/30'
                  }`} />
                  <span className="text-xs text-charcoal/70 truncate flex-1">
                    {event.event_type === 'page_view' ? event.page :
                     event.event_type === 'form_submit' ? `${event.page} form` :
                     event.event_type}
                  </span>
                  <span className="text-xs text-charcoal/40">
                    {new Date(event.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-charcoal/50 text-sm">No activity recorded yet</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-gold/10">
      <div className={`${color} p-2 rounded-lg w-fit mb-3`}>
        <Icon size={18} className="text-white" />
      </div>
      <p className="text-2xl font-semibold text-charcoal">{value.toLocaleString()}</p>
      <p className="text-sm text-charcoal/60 mt-1">{label}</p>
    </div>
  );
}

function SubmissionRow({ label, value }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-cream/50">
      <span className="text-sm text-charcoal">{label}</span>
      <span className="font-medium text-charcoal">{value}</span>
    </div>
  );
}
